import cx from 'classnames';
import { shape, string, bool } from 'prop-types';
import React, { useEffect } from 'react';
import Linkify from 'react-linkify';

import Image from '../../Image';
import UserIcon from '../../UserIcon';

import { metaActions } from '../../../store/actions';

import GroupedOffersMessage from '../GroupedOffersMessage';
import OffersMessage from '../OffersMessage';
import OrderMessage from '../OrderMessage';
import PaymentMessage from '../PaymentMessage';

import { dateFormatRU } from '../../../utils';
import { usersRoles } from '../../../constants/usersRoles';

import s from './index.module.css';
import RateExpert from '../../RateExpert';
import ExpertFeedback from '../../ExpertFeedback/ExpertFeedback';
import CarBrandSelect from '../../CarBrandSelect/CarBrandSelect';
import { ReactComponent as CheckIcon } from './assets/check.svg';
import { ReactComponent as CheckedWithBorderIcon } from './assets/checkedWithBorder.svg';
import ExpertDialogLeft from '../../ExpertDialogLeft';
import { useDispatch } from 'react-redux';

const Message = ({
  senderType, // number with type of sender from usersRoles
  userName, // string w name that should be displayed on top of the message
  time, // string w timestamp when message was created
  children, // DOM node to interpolate as content in message body
  messageContent, // string w message text
  avatarUrl, // string w url to be displayed for expert avatar
  attachments, // array of url string for displaying images under message
  offers, // Offers
  mapOffers, // Map offers
  location, // Location object for offers and order messages
  order, // Order data
  authOptions, // data for cloudpayments widget
  amount, // full price for payment button
  valid, // valid date fro payment button
  type, // Message type
  isExpertMessage, // boolean - if true than display avatar and expert name
  isAnotherSender, // boolean defines if current message is from different sender than previous
  onSelectImage, // callback w function to call on image click
  onSelectLocation, // callback w function to call on map location click
  showGroupedRows, // flag for displaying grouped offers by pvz
  detailCard, // info about detail for groupedOffersMessage
  isWidgetPath, // bool flag that indicates chat used in widget
  messageId, // message id
  score, // expert rate score
  rateExpertId, // expert id to be rated
  showFeedback, // show feedback save message
  chatWindowRef, // ref for chat window
  visible, // is message visible
  isReaded, // is message readed
  isExpertOnline, // is expert online
  isEmex // is current chat emex
}) => {
  const dispatch = useDispatch();
  const isGroupedOffersMessage = type === 'offers' && showGroupedRows;
  const isOffersMessage = type === 'offers' && !showGroupedRows;
  const isOrderMessage = type === 'order';
  const isPaymentMessage = type === 'payment';
  const isRateMessage = type === 'rate';
  const isSelectCarBrand = type === 'selectCarBrand' && visible;
  const isExpertDialogLeft = type === 'expertDialogLeft';
  const isButtonMessage = type === 'buttons';

  const isInputBlocked = isSelectCarBrand || isButtonMessage;

  useEffect(() => {
    if (isInputBlocked) {
      dispatch(metaActions.blockMessageInput);
    } else {
      dispatch(metaActions.unblockMessageInput);
    }
  }, [type]);

  const isShowMessage =
    Boolean(messageContent) || isAnotherSender || isOffersMessage || isOrderMessage || isGroupedOffersMessage;

  const linkDecorator = (href, text, key) => (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      href={href}
      key={key}
      className={cx({
        [s.buyerMessageLink]: senderType === usersRoles.BUYER,
        [s.messageLink]: senderType !== usersRoles.BUYER,
      })}
      target="_blank"
      rel="noopener noreferrer"
    >
      {text}
    </a>
  );

  const detailInfo =
    detailCard && detailCard.makeName && detailCard.detailName
      ? `${detailCard.makeName} · ${detailCard.detailName}`
      : detailCard.makeName
      ? detailCard.makeName
      : '';
  if (isRateMessage) {
    return <RateExpert rate={score} messageId={messageId} expertId={rateExpertId} />;
  }
  if (isSelectCarBrand) {
    return (
      <div className={s.selectCar}>
        <CarBrandSelect messageId={messageId} chatWindowRef={chatWindowRef}/>
      </div>
    );
  }

  if (isExpertDialogLeft) {
    return <ExpertDialogLeft />;
  }

  return (
    <Linkify componentDecorator={linkDecorator}>
      {isShowMessage && (
        <div
          className={cx(s.wrapper, {
            [s.buyerWrapper]: senderType === usersRoles.BUYER,
            [s.expertWrapper]: senderType !== usersRoles.BUYER,
            [s.anotherSender]: isAnotherSender,
            [s.widgetWrapper]: isWidgetPath,
          })}
        >
          {isExpertMessage && senderType !== usersRoles.BUYER && (
            <UserIcon isOnline={isExpertOnline} className={s.icon} icon={avatarUrl} />
          )}
          <div
            className={cx(s.message, {
              [s.emexMessage]: isEmex,
              [s.buyerMessage]: senderType === usersRoles.BUYER,
              [s.expertMessage]: senderType !== usersRoles.BUYER,
              [s.widgetMessage]: isWidgetPath,
            })}
          >
            {(isExpertMessage || isAnotherSender) && (
              <div className={s.header}>
                {senderType !== usersRoles.BUYER && <span className={s.userName}>{userName}</span>}
                {Boolean(time) && <span className={s.time}>{dateFormatRU(new Date(time), 'H:MM')}</span>}
              </div>
            )}
            {senderType === usersRoles.BUYER && (
              <div className={s.messageStatus} title={isReaded ? 'Прочитано' : 'Доставлено'}>
                <CheckedWithBorderIcon className={s.statusSended} />
                {isReaded && <CheckIcon className={s.statusReaded} />}
              </div>
            )}
            <div className={s.messageContent}>{children}</div>

            {isGroupedOffersMessage && <GroupedOffersMessage offers={offers} detailInfo={detailInfo} />}
            {isOffersMessage && (
              <OffersMessage
                offers={offers}
                mapOffers={mapOffers}
                onSelectLocation={onSelectLocation}
                location={location}
                detailInfo={detailInfo}
              />
            )}
            {isOrderMessage && <OrderMessage order={order} />}
            {isPaymentMessage && <PaymentMessage authOptions={authOptions} valid={valid} amount={amount} />}
          </div>
        </div>
      )}
      {attachments && (
        <div
          className={cx(s.attachments, {
            [s.buyerAttachments]: senderType === usersRoles.BUYER,
          })}
        >
          {attachments.map((src, index) => (
            <Image src={src} key={index} onSelect={() => onSelectImage(index)} />
          ))}
        </div>
      )}
      {showFeedback && <ExpertFeedback chatWindowRef={chatWindowRef} messageId={messageId} expertId={rateExpertId} />}
    </Linkify>
  );
};

Message.propTypes = {
  detailCard: shape({
    makeName: string,
    detailName: string,
    detailNum: string,
    imageUrl: string,
    imageSent: bool,
  }),
};

Message.defaultProps = {
  detailCard: {},
};

export default Message;
